/**
 * Sidebar.jsx
 * Premium collapsible left sidebar.
 * 260px expanded → 68px collapsed.
 * Gold active indicator, smooth animations.
 */
import React from "react";
import {
  LayoutDashboard, Radio, CalendarDays, TrendingUp,
  Wallet, Home, ScrollText, Bell, Settings,
  PlusCircle, ChevronLeft, ChevronRight, Building2,
  ArrowLeftRight,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    label: "Main",
    items: [
      { key: "dashboard",  label: "Dashboard",    icon: LayoutDashboard },
      { key: "monitor",    label: "Daily Monitor", icon: Radio },
      { key: "calendar",   label: "Calendar",      icon: CalendarDays },
      { key: "analytics",  label: "Analytics",     icon: TrendingUp },
    ],
  },
  {
    label: "Manage",
    items: [
      { key: "add",        label: "New Booking",   icon: PlusCircle },
      { key: "finance",    label: "Finance",        icon: Wallet },
      { key: "properties", label: "Properties",    icon: Home },
    ],
  },
  {
    label: "System",
    items: [
      { key: "activity",   label: "Activity Log",  icon: ScrollText },
      { key: "requests",   label: "Join Requests", icon: Bell, badge: true },
      { key: "settings",   label: "Settings",      icon: Settings, adminOnly: true },
    ],
  },
];

export default function Sidebar({
  activeTab, setActiveTab,
  collapsed, setCollapsed,
  mobileOpen, setMobileOpen,
  business, isAdmin,
  pendingCount,
  onSwitchBusiness,
}) {
  const handleNav = (key) => {
    setActiveTab(key);
    if (mobileOpen) setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 49,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
          }}
        />
      )}

      <aside className={`sidebar${collapsed ? " collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}>
        {/* Logo / Business */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">🏠</div>
          <div className="sidebar-logo-text">
            <div className="sidebar-logo-name">{business?.name || "StayTrack"}</div>
            <div className="sidebar-logo-sub">Property Manager</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter(
              item => !item.adminOnly || isAdmin
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.label}>
                <div className="sidebar-section-label">{section.label}</div>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const count = item.badge ? pendingCount : 0;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleNav(item.key)}
                      className={`sidebar-item${activeTab === item.key ? " active" : ""}`}
                      title={collapsed ? item.label : ""}
                    >
                      <span className="sidebar-item-icon">
                        <Icon size={17} strokeWidth={activeTab === item.key ? 2.5 : 1.8} />
                      </span>
                      <span className="sidebar-item-label">{item.label}</span>
                      {count > 0 && (
                        <span className="sidebar-badge">{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Switch business */}
          <button
            onClick={onSwitchBusiness}
            className="sidebar-collapse-btn"
            title={collapsed ? "Switch Business" : ""}
          >
            <span style={{ display:"flex", alignItems:"center", justifyContent:"center", width:20, minWidth:20 }}>
              <ArrowLeftRight size={15} />
            </span>
            <span className="sidebar-item-label" style={{ fontSize:13, fontWeight:500 }}>Switch Business</span>
          </button>

          {/* Collapse toggle — desktop only */}
          <button
            onClick={() => setCollapsed(p => !p)}
            className="sidebar-collapse-btn"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{ display:"flex" }}
          >
            <span style={{ display:"flex", alignItems:"center", justifyContent:"center", width:20, minWidth:20 }}>
              {collapsed
                ? <ChevronRight size={15} />
                : <ChevronLeft  size={15} />
              }
            </span>
            <span className="sidebar-item-label" style={{ fontSize:13, fontWeight:500 }}>
              Collapse
            </span>
          </button>
        </div>
      </aside>
    </>
  );
}
